import { Notification } from 'bloomer'
import { Component } from 'react'
import LoadSpinner from './loadSpinner/LoadSpinner'

interface IState {
  hasError: boolean
  errorMessage: string
}

// The code of a page could not be loaded. Usually the app was open during a deploy and asks for a
// file of the previous version that no longer exists (Vite: "Failed to fetch dynamically imported
// module", "Importing a module script failed" in Safari, webpack: "Loading chunk 12 failed").
const chunkFailedMessage = /Loading chunk [\d]+ failed|dynamically imported module|Importing a module script failed|error loading dynamically imported module/i

const RELOAD_KEY = 'chunk-reload-at'

export function isChunkLoadError(error: Error) {
  return chunkFailedMessage.test(error?.message ?? '')
}

// reload once to get the current version; a second failure within a minute shows the error
export function reloadForNewVersion(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0)
    if (Date.now() - last < 60000) return false
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
  } catch {
    // no storage (private mode): reload anyway, the browser loads the new version
  }
  window.location.reload()
  return true
}

export default class AsyncErrorBoundary extends Component<{}, IState> {
  public state: IState = {
    errorMessage: '',
    hasError: false
  }

  public static getDerivedStateFromError(error: Error) {
    // until now every TypeError showed an endless spinner, which also hid real bugs
    // (e.g. the profile page "loading" forever). Only a failed code load waits for the reload.
    if (isChunkLoadError(error) && process.env.NODE_ENV !== 'development') {
      return { errorMessage: '', hasError: true }
    }
    return { hasError: true, errorMessage: error?.message || String(error) }
  }

  public componentDidCatch(error: Error) {
    if (isChunkLoadError(error) && process.env.NODE_ENV !== 'development' && !reloadForNewVersion()) {
      this.setState({ errorMessage: error.message })
    }
  }

  public render() {
    if (this.state.hasError) {
      if (!this.state.errorMessage) {
        return <LoadSpinner className="centered" />
      }

      return (
        <Notification isColor="danger">
          Ein Fehler ist aufgetreten. Bitte laden Sie die Seite neu. Wenn der Fehler bleibt, wenden Sie sich an support@alphaprocess.io
          <p>
            { this.state.errorMessage }
          </p>
        </Notification>
      )
    }
    return (
      <>
        { this.props.children }
      </>
    )
  }
}
